import { PropsWithChildren, useEffect, useRef } from "react";

import { Text } from "@vinomofo/components";
import { debounce } from "../../utils/debounce";
import styled from "styled-components";
import { useState } from "react";

const Description = styled(Text)<{ isTruncated: boolean }>`
  display: -webkit-box;
  -webkit-line-clamp: ${({ isTruncated }) => (isTruncated ? 12 : 0)};
  -webkit-box-orient: vertical;
  overflow: hidden;
  * + * {
    margin-top: 1em;
  }
  hr {
    border-top: 1px solid ${({ theme }) => theme.colors.mofoGreen};
    margin-top: 1.5em;
  }
  blockquote + blockquote {
    margin-top: 0;
  }
`;

const Button = styled.button<{ isTruncated: boolean }>`
  font: inherit;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  background: none;
  border: 0;
  outline: 0;
  padding: 0;
  cursor: pointer;
  margin-top: 15px;

  &:hover {
    text-decoration: underline;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface WineNotesDescriptionProps {}

export const WineDescription = ({
  children,
}: PropsWithChildren<WineNotesDescriptionProps>): JSX.Element => {
  const descriptionRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(true);
  const [showButton, setShowButton] = useState(true);

  useEffect(() => {
    const hasTruncation = (el) => {
      const { clientHeight, scrollHeight } = el;
      return clientHeight !== scrollHeight;
    };

    const checkButtonAvailability = () => {
      if (descriptionRef.current) {
        setShowButton(hasTruncation(descriptionRef.current));
      }
    };

    const debouncedCheck = debounce(checkButtonAvailability, 50);

    checkButtonAvailability();
    window.addEventListener("resize", debouncedCheck);

    return () => {
      window.removeEventListener("resize", debouncedCheck);
    };
  }, [descriptionRef]);

  const handleClick = () => setIsTruncated(!isTruncated);

  return (
    <Text fontSize={["14px", "16px"]} as="div">
      <Description
        ref={descriptionRef}
        isTruncated={isTruncated}
        fontSize="inherit"
        as="div"
      >
        {children}
      </Description>

      {showButton && (
        <Button onClick={handleClick}>
          Read {isTruncated ? "more" : "less"}
        </Button>
      )}
    </Text>
  );
};
